import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { CENTRAL_PLACE_ID, UserAccessApi } from '../../../common-files/user-access-api';

// give a user one more role: pick the place first (an establishment — or Central
// when the backend allows it and the role is not establishment-only), then a role.
// Self-saving; closes with `true` on success. data = { sumId, name, api }.
@Component({
  selector: 'app-assign-role-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './assign-role-dialog.component.html',
  styleUrls: ['./assign-role-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class AssignRoleDialogComponent implements OnInit {
  form: FormGroup;
  establishments: any[] = [];
  roles: any[] = [];
  isLoading: boolean = false;
  readonly centralPlaceId = CENTRAL_PLACE_ID;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AssignRoleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sumId: number; name: string; api: UserAccessApi },
    private toastService: ToastService
  ) {
    const activeEst = localStorage.getItem('activeEstablishmentId');
    this.form = this.fb.group({
      establishmentId: [activeEst ? Number(activeEst) : null, Validators.required],
      roleKey: ['', Validators.required],
      validUntil: [''],
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.data.api.listEstablishments().subscribe({
      next: (list) => (this.establishments = list),
      error: () => {},
    });
    this.data.api.listAssignableRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load roles.');
      },
    });
  }

  get selectedRole(): any {
    return this.roles.find((r) => r.roleKey === this.form.get('roleKey')?.value) || null;
  }

  // central-only roles live at Central; establishment-only roles need a branch
  get showCentral(): boolean {
    return this.data.api.allowCentralAssignment && this.selectedRole?.applicableFor !== 'ESTABLISHMENT';
  }

  get showEstablishments(): boolean {
    return this.selectedRole?.applicableFor !== 'CENTRAL';
  }

  onRoleChange(): void {
    const role = this.selectedRole;
    if (!role) return;
    if (role.applicableFor === 'CENTRAL') this.form.patchValue({ establishmentId: CENTRAL_PLACE_ID });
    else if (this.form.get('establishmentId')?.value === CENTRAL_PLACE_ID && !this.data.api.allowCentralAssignment) {
      this.form.patchValue({ establishmentId: null });
    }
  }

  // a Volunteer is given for a limited time, so an expiry date is mandatory
  get isVolunteer(): boolean {
    return this.form.get('roleKey')?.value === 'ESTAB_VOLUNTEER';
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error('Select a place and a role.');
      return;
    }
    const value = this.form.value;
    const isCentral = Number(value.establishmentId) === CENTRAL_PLACE_ID;
    if (isCentral && !this.showCentral) {
      this.toastService.error('This role must be assigned at an establishment.');
      return;
    }
    if (this.isVolunteer && !this.form.value.validUntil) {
      this.toastService.error('Volunteer role needs an expiry date.');
      return;
    }
    this.isLoading = true;
    this.data.api
      .assignRole(this.data.sumId, {
        establishmentId: isCentral ? null : Number(value.establishmentId),
        roleKey: value.roleKey,
        validUntil: value.validUntil || null,
      })
      .subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Role assigned.');
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to assign the role.');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
